<script setup>
import { computed, onMounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';

import ReportSectionCard from './ReportSectionCard.vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const uiFlags = useMapGetter('getOverviewUIFlags');
const agentStatus = useMapGetter('agents/getAgentStatus');
const accountConversationMetric = useMapGetter('getAccountConversationMetric');
const store = useStore();

const accounti18nKey = 'OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS';
const teams = useMapGetter('teams/getTeams');

// Full class strings keep Tailwind able to generate every variant.
const CONVERSATION_TILES = [
  {
    key: 'open',
    label: 'OPEN',
    icon: 'i-hugeicons-message-multiple-01',
    tone: 'bg-n-blue-3 text-n-blue-11',
  },
  {
    key: 'unattended',
    label: 'UNATTENDED',
    icon: 'i-hugeicons-message-notification-01',
    tone: 'bg-n-amber-3 text-n-amber-11',
  },
  {
    key: 'unassigned',
    label: 'UNASSIGNED',
    icon: 'i-hugeicons-user-remove-01',
    tone: 'bg-n-ruby-3 text-n-ruby-11',
  },
  {
    key: 'pending',
    label: 'PENDING',
    icon: 'i-hugeicons-clock-01',
    tone: 'bg-n-alpha-2 text-n-slate-11',
  },
];

const AGENT_STATUS_TILES = [
  { key: 'online', label: 'ONLINE', dot: 'bg-n-teal-9' },
  { key: 'busy', label: 'BUSY', dot: 'bg-n-amber-9' },
  { key: 'offline', label: 'OFFLINE', dot: 'bg-n-slate-8' },
];

const formatCount = value =>
  value === undefined || value === null ? '—' : Number(value).toLocaleString();

const conversationMetrics = computed(() =>
  CONVERSATION_TILES.map(tile => ({
    ...tile,
    name: t(`${accounti18nKey}.${tile.label}`),
    hint: t(`${accounti18nKey}.HINTS.${tile.label}`),
    value: formatCount(accountConversationMetric.value?.[tile.key]),
  }))
);

const totalAgents = computed(() =>
  AGENT_STATUS_TILES.reduce(
    (sum, tile) => sum + Number(agentStatus.value?.[tile.key] || 0),
    0
  )
);

const agentStatusMetrics = computed(() =>
  AGENT_STATUS_TILES.map(tile => {
    const count = Number(agentStatus.value?.[tile.key] || 0);
    return {
      ...tile,
      name: t(`OVERVIEW_REPORTS.AGENT_STATUS.${tile.label}`),
      value: count.toLocaleString(),
      share: totalAgents.value ? (count / totalAgents.value) * 100 : 0,
    };
  })
);

const teamMenuList = computed(() => {
  return [
    { label: t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.ALL_TEAMS'), value: null },
    ...teams.value.map(team => ({ label: team.name, value: team.id })),
  ];
});

const selectedTeam = ref(null);
const selectedTeamLabel = computed(() => {
  const team =
    teamMenuList.value.find(
      menuItem => menuItem.value === selectedTeam.value
    ) || {};
  return team.label;
});
const fetchData = () => {
  const params = {};
  if (selectedTeam.value) {
    params.team_id = selectedTeam.value;
  }
  store.dispatch('fetchAccountConversationMetric', params);
};

const { startRefetching } = useLiveRefresh(fetchData);
const [showDropdown, toggleDropdown] = useToggle();

const handleAction = ({ value }) => {
  toggleDropdown(false);
  selectedTeam.value = value;
  fetchData();
};

onMounted(() => {
  fetchData();
  startRefetching();
});
</script>

<template>
  <div
    class="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]"
  >
    <ReportSectionCard
      :title="t(`${accounti18nKey}.HEADER`)"
      :description="t(`${accounti18nKey}.DESCRIPTION`)"
      icon="i-hugeicons-message-multiple-01"
      live
      class="conversation-metric"
    >
      <template v-if="teams.length" #actions>
        <div
          v-on-clickaway="() => toggleDropdown(false)"
          class="group relative z-20 flex items-center"
        >
          <Button
            sm
            slate
            faded
            icon="i-hugeicons-user-group"
            :label="selectedTeamLabel"
            class="max-w-[14rem] rounded-md group-hover:bg-n-alpha-2"
            @click="toggleDropdown()"
          />
          <DropdownMenu
            v-if="showDropdown"
            :menu-items="teamMenuList"
            class="top-full mt-1 ltr:right-0 rtl:left-0"
            @action="handleAction($event)"
          />
        </div>
      </template>
      <div
        v-if="uiFlags.isFetchingAccountConversationMetric"
        class="grid grid-cols-2 gap-3 lg:grid-cols-4"
        role="status"
        :aria-label="t(`${accounti18nKey}.LOADING_MESSAGE`)"
      >
        <div
          v-for="tile in CONVERSATION_TILES"
          :key="tile.key"
          class="h-[6.5rem] rounded-xl bg-n-alpha-2 motion-safe:animate-pulse"
        />
      </div>
      <dl v-else class="m-0 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div
          v-for="metric in conversationMetrics"
          :key="metric.key"
          :data-test-id="`live-metric-${metric.key}`"
          class="flex min-w-0 flex-col gap-2 rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3 dark:border-n-strong"
        >
          <dt
            class="flex min-w-0 items-center gap-2 text-xs font-medium text-n-slate-11"
          >
            <span
              class="grid size-7 shrink-0 place-items-center rounded-lg"
              :class="metric.tone"
              aria-hidden="true"
            >
              <span :class="metric.icon" class="size-4" />
            </span>
            <span class="truncate">{{ metric.name }}</span>
          </dt>
          <dd class="m-0 flex flex-col gap-0.5">
            <span
              class="text-2xl font-semibold tabular-nums leading-tight text-n-slate-12 sm:text-3xl"
            >
              {{ metric.value }}
            </span>
            <span class="text-xs leading-snug text-n-slate-11">
              {{ metric.hint }}
            </span>
          </dd>
        </div>
      </dl>
    </ReportSectionCard>

    <ReportSectionCard
      :title="t('OVERVIEW_REPORTS.AGENT_STATUS.HEADER')"
      :description="
        t('OVERVIEW_REPORTS.AGENT_STATUS.DESCRIPTION', { count: totalAgents })
      "
      icon="i-hugeicons-user-status"
      live
    >
      <dl class="m-0 grid grid-cols-3 gap-3">
        <div
          v-for="metric in agentStatusMetrics"
          :key="metric.key"
          :data-test-id="`agent-status-${metric.key}`"
          class="flex min-w-0 flex-col gap-2 rounded-xl border border-n-weak bg-n-solid-1 px-3 py-3 dark:border-n-strong sm:px-4"
        >
          <dt
            class="flex min-w-0 items-center gap-2 text-xs font-medium text-n-slate-11"
          >
            <span
              class="size-2.5 shrink-0 rounded-full"
              :class="metric.dot"
              aria-hidden="true"
            />
            <span class="truncate">{{ metric.name }}</span>
          </dt>
          <dd
            class="m-0 text-2xl font-semibold tabular-nums leading-tight text-n-slate-12 sm:text-3xl"
          >
            {{ metric.value }}
          </dd>
        </div>
      </dl>
      <div
        v-if="totalAgents"
        class="mt-4 flex h-2 w-full gap-0.5 overflow-hidden rounded-full bg-n-alpha-2"
        aria-hidden="true"
      >
        <span
          v-for="metric in agentStatusMetrics"
          v-show="metric.share > 0"
          :key="metric.key"
          class="h-full first:rounded-s-full last:rounded-e-full"
          :class="metric.dot"
          :style="{ width: `${metric.share}%` }"
        />
      </div>
    </ReportSectionCard>
  </div>
</template>
